import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Icon, Input, Message, Popup } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showInfo,
  showSuccess,
  verifyJSON,
} from '../../helpers';
import { CHANNEL_OPTIONS } from '../../constants';
import { renderChannelTip } from '../../helpers/render';
import { radius } from '../../styles/globalStyles';
import { StudioFormHeader, StudioPage } from '../../components/StudioPage';
import '../Setting/Settings.css';

const MODEL_MAPPING_EXAMPLE = {
  'gpt-3.5-turbo-0301': 'gpt-3.5-turbo',
  'gpt-4-0314': 'gpt-4',
  'gpt-4-32k-0314': 'gpt-4-32k',
};

function type2secretPrompt(type, t) {
  switch (type) {
    case 15:
      return t('channel.edit.key_prompts.zhipu');
    case 18:
      return t('channel.edit.key_prompts.spark');
    case 22:
      return t('channel.edit.key_prompts.fastgpt');
    case 23:
      return t('channel.edit.key_prompts.tencent');
    default:
      return t('channel.edit.key_prompts.default');
  }
}

const EditChannel = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const channelId = params.id;
  const isEdit = channelId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modelImportLoading, setModelImportLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const handleCancel = () => {
    navigate('/channel');
  };

  const originInputs = {
    name: '',
    type: 1,
    key: '',
    base_url: '',
    other: '',
    model_mapping: '',
    system_prompt: '',
    models: [],
    groups: ['default'],
    weight: 0,
    enable_encryption: false,
  };
  const [batch, setBatch] = useState(false);
  const [inputs, setInputs] = useState(originInputs);
  // 保存编辑渠道时后端返回的掩码密钥，用于提交时判断密钥是否被用户修改
  // 如果用户未修改密钥，则提交掩码值让后端跳过 key 更新
  const [originalKey, setOriginalKey] = useState('');
  const [originModelOptions, setOriginModelOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [customModel, setCustomModel] = useState('');
  const [proxySubscriptionOptions, setProxySubscriptionOptions] = useState([]);
  const [proxySubscriptionLoading, setProxySubscriptionLoading] = useState(false);
  const [proxyNodeOptions, setProxyNodeOptions] = useState([]);
  const [proxyNodeLoading, setProxyNodeLoading] = useState(false);
  const [proxyNodeTesting, setProxyNodeTesting] = useState(false);
  const [config, setConfig] = useState({
    region: '',
    sk: '',
    ak: '',
    user_id: '',
    vertex_ai_project_id: '',
    vertex_ai_adc: '',
    remove_max_tokens: false,
    proxy_subscription_id: 0,
    proxy_node_id: 0,
  });
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleConfigChange = (e, { name, value }) => {
    setConfig((inputs) => ({ ...inputs, [name]: value }));
  };

  const loadChannel = async () => {
    let res = await API.get(`/api/channel/${channelId}`);
    const { success, message, data } = res.data;
    if (success) {
      if (data.models === '') {
        data.models = [];
      } else {
        data.models = data.models.split(',');
      }
      if (data.group === '') {
        data.groups = [];
      } else {
        data.groups = data.group.split(',');
      }
      // weight 后端为 *uint，未设置时为 null，统一为 0
      if (data.weight === null || data.weight === undefined) {
        data.weight = 0;
      }
      if (data.model_mapping !== '') {
        data.model_mapping = JSON.stringify(
          JSON.parse(data.model_mapping),
          null,
          2
        );
      }
      setInputs(data);
      // 保存后端返回的掩码密钥，用于编辑提交时判断密钥是否被修改
      setOriginalKey(data.key);
      if (data.config !== '') {
        const parsedConfig = JSON.parse(data.config);
        setConfig(parsedConfig);
        // 编辑模式下，如果已有代理订阅，预加载节点列表
        if (parsedConfig.proxy_enabled && parsedConfig.proxy_subscription_id > 0) {
          fetchProxyNodes(parsedConfig.proxy_subscription_id);
        }
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const fetchModels = async () => {
    try {
      let res = await API.get(`/api/channel/models`);
      let localModelOptions = res.data.data.map((model) => ({
        key: model.id,
        text: model.id,
        value: model.id,
      }));
      setOriginModelOptions(localModelOptions);
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(
        res.data.data.map((group) => ({
          key: group,
          text: group,
          value: group,
        }))
      );
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchProxySubscriptions = async () => {
    setProxySubscriptionLoading(true);
    try {
      let res = await API.get(`/api/proxy-subscription/`);
      const { success, data } = res.data;
      if (success && Array.isArray(data)) {
        const options = data.map((sub) => ({
          key: sub.id,
          text: sub.name,
          value: sub.id,
        }));
        // 始终将"不指定（使用全局）"选项放在第一位
        options.unshift({
          key: 0,
          text: t('channel.edit.proxy_subscription_none'),
          value: 0,
        });
        setProxySubscriptionOptions(options);
      }
    } catch (error) {
      // 静默处理，不影响页面加载
      console.warn('Failed to load proxy subscriptions:', error.message);
    } finally {
      setProxySubscriptionLoading(false);
    }
  };

  const fetchProxyNodes = async (subscriptionId) => {
    if (!subscriptionId || subscriptionId <= 0) {
      setProxyNodeOptions([]);
      return;
    }
    setProxyNodeLoading(true);
    try {
      let res = await API.get(`/api/proxy-subscription/${subscriptionId}/nodes`);
      const { success, data } = res.data;
      if (success && Array.isArray(data)) {
        const options = [
          {
            key: 0,
            text: t('channel.edit.proxy_node_all'),
            value: 0,
          },
          ...data.map((node) => {
            const addr = `${node.protocol}://${node.host}:${node.port}`;
            let latencyText;
            if (
              (node.test_status === 'success' || node.test_status === 'ok') &&
              node.test_latency_ms != null &&
              node.test_latency_ms > 0
            ) {
              latencyText = `${node.test_latency_ms}ms`;
            } else if (node.test_status === 'failed') {
              latencyText = t('channel.edit.proxy_node_failed');
            } else {
              latencyText = '-';
            }
            return {
              key: node.id,
              text: `${node.name} (${addr}) - ${latencyText}`,
              value: node.id,
            };
          }),
        ];
        setProxyNodeOptions(options);
      } else {
        setProxyNodeOptions([
          {
            key: 0,
            text: t('channel.edit.proxy_node_all'),
            value: 0,
          },
        ]);
      }
    } catch (error) {
      console.warn('Failed to load proxy nodes:', error.message);
      setProxyNodeOptions([
        {
          key: 0,
          text: t('channel.edit.proxy_node_all'),
          value: 0,
        },
      ]);
    } finally {
      setProxyNodeLoading(false);
    }
  };

  const testProxyNodes = async () => {
    const subscriptionId = config.proxy_subscription_id;
    if (!subscriptionId || subscriptionId <= 0 || proxyNodeTesting) return;
    setProxyNodeTesting(true);
    try {
      const res = await API.post(
        `/api/proxy-subscription/${subscriptionId}/nodes/test`
      );
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('channel.edit.proxy_test_started'));
        // 延迟后刷新节点列表，给后端拨测留时间
        setTimeout(() => {
          fetchProxyNodes(subscriptionId);
        }, 5000);
      } else {
        showError(message || t('channel.edit.proxy_test_failed'));
      }
    } catch (error) {
      showError(error.message || t('channel.edit.proxy_test_failed'));
    } finally {
      setProxyNodeTesting(false);
    }
  };

  useEffect(() => {
    let localModelOptions = [...originModelOptions];
    inputs.models.forEach((model) => {
      if (!localModelOptions.find((option) => option.key === model)) {
        localModelOptions.push({
          key: model,
          text: model,
          value: model,
        });
      }
    });
    setModelOptions(localModelOptions);
  }, [originModelOptions, inputs.models]);

  useEffect(() => {
    if (isEdit) {
      loadChannel().then();
    }
    fetchModels().then();
    fetchGroups().then();
    fetchProxySubscriptions().then();
  }, []);

  const submit = async () => {
    if (submitLoading || modelImportLoading || statusLoading) return;

    let localInputs = { ...inputs };
    // AWS (type 33) 和 Vertex AI (type 42) 渠道的 key 从 config 的 AK/SK/Region 组装
    // 编辑时 inputs.key 是掩码值，必须清空才能进入下面的组装逻辑
    if (isEdit && (localInputs.type === 33 || localInputs.type === 42)) {
      localInputs.key = '';
    }
    if (localInputs.key === '') {
      if (config.ak !== '' && config.sk !== '' && config.region !== '') {
        localInputs.key = `${config.ak}|${config.sk}|${config.region}`;
      } else if (
        config.region !== '' &&
        config.vertex_ai_project_id !== '' &&
        config.vertex_ai_adc !== ''
      ) {
        localInputs.key = `${config.region}|${config.vertex_ai_project_id}|${config.vertex_ai_adc}`;
      }
    }
    if (!isEdit && (localInputs.name === '' || localInputs.key === '')) {
      showInfo(t('channel.edit.messages.name_required'));
      return;
    }
    // 编辑模式下，如果 key 为空（用户未修改或主动清空），则恢复为掩码值
    // 这样后端检测到掩码值后会跳过 key 字段的更新，保留原始密钥
    if (isEdit && localInputs.key === '') {
      localInputs.key = originalKey;
    }
    if (localInputs.type !== 43 && localInputs.models.length === 0) {
      showInfo(t('channel.edit.messages.models_required'));
      return;
    }
    if (
      localInputs.model_mapping !== '' &&
      !verifyJSON(localInputs.model_mapping)
    ) {
      showInfo(t('channel.edit.messages.model_mapping_invalid'));
      return;
    }
    if (localInputs.key === 'undefined|undefined|undefined') {
      localInputs.key = ''; // prevent potential bug
    }
    if (localInputs.base_url && localInputs.base_url.endsWith('/')) {
      localInputs.base_url = localInputs.base_url.slice(
        0,
        localInputs.base_url.length - 1
      );
    }
    if (localInputs.type === 3 && localInputs.other === '') {
      localInputs.other = '2024-03-01-preview';
    }
    localInputs.models = localInputs.models.join(',');
    localInputs.group = localInputs.groups.join(',');
    localInputs.weight = parseInt(localInputs.weight) || 0;
    localInputs.config = JSON.stringify(config);

    setSubmitLoading(true);
    try {
      const res = isEdit
        ? await API.put(`/api/channel/`, {
            ...localInputs,
            id: parseInt(channelId),
          })
        : await API.post(`/api/channel/`, localInputs);
      const { success, message } = res.data;
      if (success) {
        if (isEdit) {
          showSuccess(t('channel.edit.messages.update_success'));
        } else {
          showSuccess(t('channel.edit.messages.create_success'));
          setInputs(originInputs);
        }
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const importEndpointModels = async () => {
    if (!isEdit || modelImportLoading || submitLoading || statusLoading) return;

    setModelImportLoading(true);
    try {
      const res = await API.get(`/api/channel/fetch_models/${channelId}`);
      const { success, message, data } = res.data;
      if (!success) {
        showError(message || t('channel.edit.messages.import_failed'));
        return;
      }

      const models = [
        ...new Set(
          (Array.isArray(data) ? data : [])
            .filter((model) => typeof model === 'string')
            .map((model) => model.trim())
            .filter(Boolean)
        ),
      ].sort((left, right) => left.localeCompare(right));
      if (models.length === 0) {
        showInfo(t('channel.edit.messages.import_empty'));
        return;
      }

      setInputs((current) => ({ ...current, models }));
      showSuccess(
        t('channel.edit.messages.import_success', { count: models.length })
      );
    } catch (error) {
      showError(error.message || t('channel.edit.messages.import_failed'));
    } finally {
      setModelImportLoading(false);
    }
  };

  const updateStatus = async (status) => {
    if (statusLoading || submitLoading || modelImportLoading) return;

    setStatusConfirmOpen(false);
    setStatusLoading(true);
    try {
      const res = await API.put(`/api/channel/status/${channelId}`, { status });
      const { success, message } = res.data;
      if (success) {
        setInputs((current) => ({ ...current, status }));
        showSuccess(t('channel.messages.operation_success'));
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setStatusLoading(false);
    }
  };

  const addCustomModel = () => {
    const model = customModel.trim();
    if (model === '') return;
    if (inputs.models.includes(model)) {
      setCustomModel('');
      return;
    }
    handleInputChange(null, {
      name: 'models',
      value: [...inputs.models, model],
    });
    setCustomModel('');
  };

  return (
    <StudioPage className='studio-channel-form-page'>
      <div className='studio-surface studio-form-shell'>
        <div style={{ padding: 24 }}>
          <StudioFormHeader
            icon={isEdit ? 'edit outline' : 'plus'}
            title={
              isEdit
                ? t('channel.edit.title_edit')
                : t('channel.edit.title_create')
            }
            description={t('channel.edit.description')}
          />
          <Form loading={loading} autoComplete='new-password' onSubmit={submit}>
            <Form.Field>
              <Form.Select
                label={t('channel.edit.type')}
                name='type'
                required
                search
                options={CHANNEL_OPTIONS}
                value={inputs.type}
                onChange={handleInputChange}
                style={{ borderRadius: 10 }}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('channel.edit.name')}
                name='name'
                placeholder={t('channel.edit.name_placeholder')}
                onChange={handleInputChange}
                value={inputs.name}
                required
                style={{ borderRadius: 10 }}
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('channel.edit.group')}
                placeholder={t('channel.edit.group_placeholder')}
                name='groups'
                required
                fluid
                multiple
                selection
                allowAdditions
                additionLabel={t('channel.edit.group_addition')}
                onChange={handleInputChange}
                value={inputs.groups}
                autoComplete='new-password'
                options={groupOptions}
                style={{ borderRadius: 10 }}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={
                  <label>
                    {t('channel.edit.weight')}
                    <span className='channel-weight-hint'>
                      {t('channel.edit.weight_hint')}
                    </span>
                  </label>
                }
                name='weight'
                type='number'
                min='0'
                step='1'
                placeholder={t('channel.edit.weight_placeholder')}
                onChange={handleInputChange}
                value={inputs.weight}
                autoComplete='new-password'
                style={{ borderRadius: 10 }}
              />
            </Form.Field>
            {renderChannelTip(inputs.type)}

            {/* Azure OpenAI specific fields */}
            {inputs.type === 3 && (
              <>
                <Message>
                  注意，<strong>模型部署名称必须和模型名称保持一致</strong>
                  ，因为 Catfee API 会把请求体中的 model
                  参数替换为你的部署名称（模型名称中的点会被剔除），
                  <a
                    target='_blank'
                    href='https://github.com/songquanpeng/one-api/issues/133?notification_referrer_id=NT_kwDOAmJSYrM2NjIwMzI3NDgyOjM5OTk4MDUw#issuecomment-1571602271'
                  >
                    图片演示
                  </a>
                  。
                </Message>
                <Form.Field>
                  <Form.Input
                    label='AZURE_OPENAI_ENDPOINT'
                    name='base_url'
                    placeholder='请输入 AZURE_OPENAI_ENDPOINT，例如：https://docs-test-001.openai.azure.com'
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                    style={{ borderRadius: 10 }}
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label='默认 API 版本'
                    name='other'
                    placeholder='请输入默认 API 版本，例如：2024-03-01-preview，该配置可以被实际的请求查询参数所覆盖'
                    onChange={handleInputChange}
                    value={inputs.other}
                    autoComplete='new-password'
                    style={{ borderRadius: 10 }}
                  />
                </Form.Field>
              </>
            )}

            {/* Custom base URL field */}
            {inputs.type === 8 && (
              <Form.Field>
                <Form.Input
                  required
                  label={t('channel.edit.proxy_url')}
                  name='base_url'
                  placeholder={t('channel.edit.proxy_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            {/* API 格式选择：自定义渠道和 OpenAI 兼容渠道支持 OpenAI / Anthropic 格式 */}
            {(inputs.type === 8 || inputs.type === 50) && (
              <>
                <Form.Field>
                  <Form.Select
                    label='API 格式'
                    name='api_format'
                    options={[
                      { key: 'openai', text: 'OpenAI 格式', value: 'openai' },
                      {
                        key: 'anthropic',
                        text: 'Anthropic 格式',
                        value: 'anthropic',
                      },
                    ]}
                    value={config.api_format || 'openai'}
                    onChange={handleConfigChange}
                    style={{ borderRadius: 10 }}
                  />
                </Form.Field>
                {(config.api_format || 'openai') === 'openai' && (
                  <div className={`channel-compat-card${config.remove_max_tokens ? ' is-active' : ''}`}>
                    <div className='channel-compat-card__icon'>
                      <Icon name='sliders horizontal' />
                    </div>
                    <div className='channel-compat-card__content'>
                      <div className='channel-compat-card__title'>
                        <strong>请求参数兼容</strong>
                        <span>OpenAI 兼容节点</span>
                      </div>
                      <p>部分上游不接受旧版 max_tokens 参数，可在转发前自动移除。</p>
                      {config.remove_max_tokens && (
                        <div className='channel-compat-card__notice'>
                          <Icon name='info circle' />
                          输出长度将由上游控制，可能增加响应耗时和额度消耗。
                        </div>
                      )}
                    </div>
                    <Form.Checkbox
                      toggle
                      name='remove_max_tokens'
                      checked={!!config.remove_max_tokens}
                      onChange={(e, { name, checked }) => {
                        setConfig((prev) => ({ ...prev, [name]: checked }));
                      }}
                    />
                  </div>
                )}
              </>
            )}
            {inputs.type === 50 && (
              <Form.Field>
                <Form.Input
                  required
                  label={t('channel.edit.base_url')}
                  name='base_url'
                  placeholder={t('channel.edit.base_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}

            {inputs.type === 18 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.spark_version')}
                  name='other'
                  placeholder={t('channel.edit.spark_version_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            {inputs.type === 21 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.knowledge_id')}
                  name='other'
                  placeholder={t('channel.edit.knowledge_id_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            {inputs.type === 17 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.plugin_param')}
                  name='other'
                  placeholder={t('channel.edit.plugin_param_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Message>{t('channel.edit.coze_notice')}</Message>
            )}
            {inputs.type === 40 && (
              <Message>
                {t('channel.edit.douban_notice')}
                <a
                  target='_blank'
                  href='https://console.volcengine.com/ark/region:ark+cn-beijing/endpoint'
                >
                  {t('channel.edit.douban_notice_link')}
                </a>
                {t('channel.edit.douban_notice_2')}
              </Message>
            )}
            {inputs.type !== 43 && (
              <section className='channel-model-section'>
                <div className='channel-model-section__header'>
                  <div className='channel-model-section__copy'>
                    <span className='studio-section-eyebrow'>
                      {t('channel.edit.models_eyebrow')}
                    </span>
                    <h2>{t('channel.edit.models_title')}</h2>
                    <p>{t('channel.edit.models_description')}</p>
                  </div>
                  <div className='channel-model-section__actions'>
                    <span className='channel-model-count'>
                      {t('channel.edit.models_count', {
                        count: inputs.models.length,
                      })}
                    </span>
                    {isEdit && (
                      <Button
                        type='button'
                        primary
                        icon='cloud download'
                        content={t(
                          modelImportLoading
                            ? 'channel.edit.buttons.importing_models'
                            : 'channel.edit.buttons.import_models'
                        )}
                        title={t('channel.edit.buttons.import_models_tip')}
                        loading={modelImportLoading}
                        disabled={
                          modelImportLoading || submitLoading || statusLoading
                        }
                        onClick={importEndpointModels}
                      />
                    )}
                  </div>
                </div>

                <Form.Field className='channel-model-field'>
                  <Form.Dropdown
                    label={t('channel.edit.models')}
                    placeholder={t('channel.edit.models_placeholder')}
                    name='models'
                    required
                    fluid
                    multiple
                    search
                    onLabelClick={(e, { value }) => {
                      copy(value).then();
                    }}
                    selection
                    disabled={modelImportLoading}
                    onChange={handleInputChange}
                    value={inputs.models}
                    autoComplete='new-password'
                    options={modelOptions}
                    style={{ borderRadius: 10 }}
                  />
                </Form.Field>

                <div className='channel-model-toolbar'>
                  <div className='channel-model-toolbar__composer'>
                    <Input
                      className='channel-model-toolbar__custom'
                      placeholder={t('channel.edit.buttons.custom_placeholder')}
                      value={customModel}
                      disabled={modelImportLoading}
                      onChange={(e, { value }) => {
                        setCustomModel(value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addCustomModel();
                          e.preventDefault();
                        }
                      }}
                    />
                    <Button
                      type='button'
                      className='channel-model-toolbar__add'
                      icon='plus'
                      content={t('channel.edit.buttons.add_custom')}
                      disabled={modelImportLoading || customModel.trim() === ''}
                      onClick={addCustomModel}
                    />
                  </div>
                  <div className='channel-model-toolbar__danger'>
                    <Button
                      type='button'
                      basic
                      negative
                      icon='trash alternate outline'
                      content={t('channel.edit.buttons.clear')}
                      title={t('channel.edit.buttons.clear_tip')}
                      disabled={
                        modelImportLoading || inputs.models.length === 0
                      }
                      onClick={() => {
                        handleInputChange(null, { name: 'models', value: [] });
                      }}
                    />
                  </div>
                </div>
                {isEdit && (
                  <p className='channel-model-section__hint'>
                    {t('channel.edit.import_models_hint')}
                  </p>
                )}
              </section>
            )}
            {inputs.type !== 43 && (
              <>
                <div
                  className={`channel-proxy-card${config.proxy_enabled ? ' is-active' : ''}`}
                >
                  <div className='channel-proxy-card__icon'>
                    <Icon name='shield' />
                  </div>
                  <div className='channel-proxy-card__content'>
                    <div className='channel-proxy-card__title'>
                      <strong>{t('channel.edit.proxy_enabled')}</strong>
                      <span>Proxy</span>
                    </div>
                    <p>{t('channel.edit.proxy_enabled_notice')}</p>
                    {config.proxy_enabled && (
                      <div className='channel-proxy-card__notice'>
                        <Icon name='info circle' />
                        {t('channel.edit.proxy_enabled_notice')}
                      </div>
                    )}
                  </div>
                  <Form.Checkbox
                    toggle
                    name='proxy_enabled'
                    checked={!!config.proxy_enabled}
                    onChange={(e, { name, checked }) => {
                      setConfig((prev) => ({ ...prev, [name]: checked }));
                    }}
                  />
                </div>
                <div
                  className={`channel-proxy-fields${config.proxy_enabled ? ' is-open' : ''}`}
                >
                  <div className='channel-proxy-fields__inner'>
                    <div className='channel-proxy-fields__row'>
                      <Form.Dropdown
                        label={t('channel.edit.proxy_subscription')}
                        placeholder={t(
                          'channel.edit.proxy_subscription_placeholder'
                        )}
                        name='proxy_subscription_id'
                        fluid
                        selection
                        search
                        loading={proxySubscriptionLoading}
                        options={proxySubscriptionOptions}
                        value={config.proxy_subscription_id ?? 0}
                        onChange={(e, { name, value }) => {
                          const subId =
                            value === '' || value === null ? 0 : value;
                          setConfig((prev) => ({
                            ...prev,
                            [name]: subId,
                            // 切换订阅时清空节点选择
                            proxy_node_id: 0,
                          }));
                          setProxyNodeOptions([]);
                          if (subId > 0) {
                            fetchProxyNodes(subId);
                          }
                        }}
                        style={{ borderRadius: 10, fontSize: 13 }}
                      />
                      {(config.proxy_subscription_id ?? 0) > 0 && (
                        <div className='channel-proxy-node-group'>
                          <div className='channel-proxy-node-group__select'>
                            <Form.Dropdown
                              label={t('channel.edit.proxy_node')}
                              placeholder={t('channel.edit.proxy_node_placeholder')}
                              name='proxy_node_id'
                              fluid
                              selection
                              search
                              loading={proxyNodeLoading}
                              options={proxyNodeOptions}
                              value={config.proxy_node_id ?? 0}
                              onChange={(e, { name, value }) => {
                                setConfig((prev) => ({
                                  ...prev,
                                  [name]:
                                    value === '' || value === null ? 0 : value,
                                }));
                              }}
                              style={{ borderRadius: 10, fontSize: 13 }}
                            />
                          </div>
                          <Button
                            type='button'
                            icon='heartbeat'
                            size='small'
                            basic
                            loading={proxyNodeTesting}
                            disabled={proxyNodeTesting || proxyNodeLoading}
                            onClick={testProxyNodes}
                            title={t('channel.edit.proxy_test_nodes')}
                            className='channel-proxy-test-btn'
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className={`channel-proxy-card channel-encryption-card${inputs.enable_encryption ? ' is-active' : ''}`}
                >
                  <div className='channel-proxy-card__icon' style={{ background: '#eff6ff', color: '#2563eb' }}>
                    <Icon name='lock' />
                  </div>
                  <div className='channel-proxy-card__content'>
                    <div className='channel-proxy-card__title'>
                      <strong>加密通讯</strong>
                      <span style={{ background: '#eff6ff', color: '#2563eb' }}>Encryption</span>
                    </div>
                    <p>自动识别请求中的敏感信息并加密为占位符，转发给下游；响应返回时自动解密回明文。</p>
                    {inputs.enable_encryption && (
                      <div className='channel-proxy-card__notice' style={{ color: '#1d4ed8' }}>
                        <Icon name='info circle' />
                        下游渠道无需改造，透明传输。
                      </div>
                    )}
                  </div>
                  <Form.Checkbox
                    toggle
                    name='enable_encryption'
                    checked={!!inputs.enable_encryption}
                    onChange={(e, { name, checked }) => {
                      setInputs((prev) => ({ ...prev, [name]: checked }));
                    }}
                  />
                </div>
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.model_mapping')}
                    placeholder={`${t(
                      'channel.edit.model_mapping_placeholder'
                    )}\n${JSON.stringify(MODEL_MAPPING_EXAMPLE, null, 2)}`}
                    name='model_mapping'
                    onChange={handleInputChange}
                    value={inputs.model_mapping}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                      borderRadius: 10,
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.system_prompt')}
                    placeholder={t('channel.edit.system_prompt_placeholder')}
                    name='system_prompt'
                    onChange={handleInputChange}
                    value={inputs.system_prompt}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                      borderRadius: 10,
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}
            {inputs.type === 33 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.aws_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                  style={{ borderRadius: 10 }}
                />
                <Form.Input
                  label='AK'
                  name='ak'
                  required
                  placeholder={t('channel.edit.aws_ak_placeholder')}
                  onChange={handleConfigChange}
                  value={config.ak}
                  autoComplete=''
                  style={{ borderRadius: 10 }}
                />
                <Form.Input
                  label='SK'
                  name='sk'
                  required
                  placeholder={t('channel.edit.aws_sk_placeholder')}
                  onChange={handleConfigChange}
                  value={config.sk}
                  autoComplete=''
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            {inputs.type === 42 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.vertex_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                  style={{ borderRadius: 10 }}
                />
                <Form.Input
                  label={t('channel.edit.vertex_project_id')}
                  name='vertex_ai_project_id'
                  required
                  placeholder={t('channel.edit.vertex_project_id_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_project_id}
                  autoComplete=''
                  style={{ borderRadius: 10 }}
                />
                <Form.Input
                  label={t('channel.edit.vertex_credentials')}
                  name='vertex_ai_adc'
                  required
                  placeholder={t('channel.edit.vertex_credentials_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_adc}
                  autoComplete=''
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Form.Input
                label={t('channel.edit.user_id')}
                name='user_id'
                required
                placeholder={t('channel.edit.user_id_placeholder')}
                onChange={handleConfigChange}
                value={config.user_id}
                autoComplete=''
                style={{ borderRadius: 10 }}
              />
            )}
            {inputs.type !== 33 &&
              inputs.type !== 42 &&
              (batch ? (
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={t('channel.edit.batch_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.key}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                      borderRadius: 10,
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
              ) : (
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={
                      isEdit
                        ? t('channel.edit.key_prompts.edit_hint', {
                            defaultValue:
                              '已保存密钥（掩码显示），留空则不修改',
                          })
                        : type2secretPrompt(inputs.type, t)
                    }
                    onChange={handleInputChange}
                    onFocus={(e) => {
                      // 编辑模式下如果 key 是掩码值，聚焦时自动全选
                      // 方便用户直接输入新值覆盖掩码值，避免在掩码值上追加
                      if (isEdit && inputs.key && inputs.key.includes('*')) {
                        e.target.select();
                      }
                    }}
                    value={inputs.key}
                    autoComplete='new-password'
                    style={{ borderRadius: 10 }}
                  />
                </Form.Field>
              ))}
            {inputs.type === 37 && (
              <Form.Field>
                <Form.Input
                  label='Account ID'
                  name='user_id'
                  required
                  placeholder={
                    '请输入 Account ID，例如：d8d7c61dbc334c32d3ced580e4bf42b4'
                  }
                  onChange={handleConfigChange}
                  value={config.user_id}
                  autoComplete=''
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            {inputs.type !== 33 && !isEdit && (
              <Form.Checkbox
                checked={batch}
                label={t('channel.edit.batch')}
                name='batch'
                onChange={() => setBatch(!batch)}
              />
            )}
            {inputs.type !== 3 &&
              inputs.type !== 33 &&
              inputs.type !== 8 &&
              inputs.type !== 50 &&
              inputs.type !== 22 && (
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.proxy_url')}
                    name='base_url'
                    placeholder={t('channel.edit.proxy_url_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                    style={{ borderRadius: 10 }}
                  />
                </Form.Field>
              )}
            {inputs.type === 22 && (
              <Form.Field>
                <Form.Input
                  label='私有部署地址'
                  name='base_url'
                  placeholder={
                    '请输入私有部署地址，格式为：https://fastgpt.run/api/openapi'
                  }
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                  style={{ borderRadius: 10 }}
                />
              </Form.Field>
            )}
            <div className='studio-form-actions channel-form-actions'>
              {isEdit && (
                <div className='channel-status-control'>
                  <div
                    className={`channel-status-indicator channel-status-indicator--${
                      inputs.status === 1
                        ? 'enabled'
                        : inputs.status === 3
                        ? 'automatic'
                        : 'disabled'
                    }`}
                    role='status'
                  >
                    <span className='channel-status-indicator__dot' />
                    <span>
                      {t(
                        inputs.status === 1
                          ? 'channel.edit.status.enabled'
                          : inputs.status === 3
                          ? 'channel.edit.status.auto_disabled'
                          : 'channel.edit.status.disabled'
                      )}
                    </span>
                  </div>
                  {inputs.status === 1 ? (
                    <Popup
                      on='click'
                      position='top left'
                      open={statusConfirmOpen}
                      onOpen={() => setStatusConfirmOpen(true)}
                      onClose={() => setStatusConfirmOpen(false)}
                      trigger={
                        <Button
                          type='button'
                          basic
                          negative
                          size='small'
                          icon='ban'
                          content={t('channel.buttons.disable')}
                          loading={statusLoading}
                          disabled={
                            statusLoading || submitLoading || modelImportLoading
                          }
                        />
                      }
                      content={
                        <div className='channel-status-confirm'>
                          <strong>
                            {t('channel.edit.status.disable_confirm_title')}
                          </strong>
                          <p>
                            {t(
                              'channel.edit.status.disable_confirm_description'
                            )}
                          </p>
                          <div className='channel-status-confirm__actions'>
                            <Button
                              type='button'
                              basic
                              size='tiny'
                              onClick={() => setStatusConfirmOpen(false)}
                            >
                              {t('channel.edit.buttons.cancel')}
                            </Button>
                            <Button
                              type='button'
                              negative
                              size='tiny'
                              icon='ban'
                              content={t('channel.edit.status.confirm_disable')}
                              onClick={() => updateStatus(2)}
                            />
                          </div>
                        </div>
                      }
                    />
                  ) : (
                    <Button
                      type='button'
                      basic
                      positive
                      size='small'
                      icon='check'
                      content={t('channel.buttons.enable')}
                      loading={statusLoading}
                      disabled={statusLoading || submitLoading}
                      onClick={() => updateStatus(1)}
                    />
                  )}
                </div>
              )}
              <div className='channel-form-actions__primary'>
                <Button
                  type='button'
                  basic
                  icon='arrow left'
                  content={t('channel.edit.buttons.cancel')}
                  disabled={
                    submitLoading || statusLoading || modelImportLoading
                  }
                  onClick={handleCancel}
                />
                <Button
                  type='submit'
                  primary
                  icon='save outline'
                  content={
                    submitLoading
                      ? t('channel.edit.buttons.saving')
                      : t(
                          isEdit
                            ? 'channel.edit.buttons.save'
                            : 'channel.edit.buttons.create'
                        )
                  }
                  loading={submitLoading}
                  disabled={
                    submitLoading || statusLoading || modelImportLoading
                  }
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </StudioPage>
  );
};

export default EditChannel;
